// Client-only "Lookout List" - orders a user flags while reviewing the
// Production Report so they can jump back to them later. No DB table;
// persisted in localStorage until they remove items themselves, and synced
// across mounted components (and tabs) via a custom event + storage event,
// matching the wigTransferCompleted/orderAutoCompleted pattern used elsewhere.
//
// Namespaced per logged-in user (see getCurrentUser in utils/auth) so two
// people sharing the same browser/computer don't see each other's list.
// This is still purely local to the browser - it does not follow a user
// across devices, since that would require server-side storage.
import { useState, useEffect, useCallback } from 'react';
import { getCurrentUser } from './auth';

const STORAGE_PREFIX = 'lookoutListItems';
const EVENT_NAME = 'lookoutListChanged';

const getStorageKey = () => `${STORAGE_PREFIX}_${getCurrentUser()}`;

const readList = () => {
  try {
    const raw = localStorage.getItem(getStorageKey());
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    console.error('Error reading lookout list:', error);
    return [];
  }
};

const writeList = (items) => {
  try {
    localStorage.setItem(getStorageKey(), JSON.stringify(items));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: items }));
  } catch (error) {
    console.error('Error saving lookout list:', error);
  }
  return items;
};

export const isOnLookoutList = (orderNo) =>
  readList().some((item) => item.orderNo === orderNo);

export const addToLookoutList = (entry) => {
  const items = readList();
  if (items.some((item) => item.orderNo === entry.orderNo)) return items;
  return writeList([...items, { ...entry, addedAt: new Date().toISOString() }]);
};

export const removeFromLookoutList = (orderNo) =>
  writeList(readList().filter((item) => item.orderNo !== orderNo));

export const toggleLookoutList = (entry) =>
  isOnLookoutList(entry.orderNo)
    ? removeFromLookoutList(entry.orderNo)
    : addToLookoutList(entry);

export const clearLookoutList = () => writeList([]);

// Hook for components that display or mutate the list (PastDueTasks page,
// bookmark buttons on the Production Report rows).
export const useLookoutList = () => {
  const [items, setItems] = useState(readList);

  useEffect(() => {
    const handleChange = (event) => setItems(event.detail || readList());
    const handleStorage = (event) => {
      if (event.key === getStorageKey()) setItems(readList());
    };
    window.addEventListener(EVENT_NAME, handleChange);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, handleChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const toggle = useCallback((entry) => setItems(toggleLookoutList(entry)), []);
  const remove = useCallback((orderNo) => setItems(removeFromLookoutList(orderNo)), []);
  const clear = useCallback(() => setItems(clearLookoutList()), []);
  const isBookmarked = useCallback(
    (orderNo) => items.some((item) => item.orderNo === orderNo),
    [items]
  );

  return { items, toggle, remove, clear, isBookmarked };
};
